/* Luxe game sound kit — velvet card swishes, soft taps, FM bells and harp plucks
   through a warm hall reverb. Shared by the Echo and Harmony games. */

const reverbs = new WeakMap();

const impulse = (ctx, seconds = 2.4) => {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
  }
  return buf;
};

/* Dry + reverb bus feeding `out`. */
export const makeBus = (ctx, out) => {
  if (reverbs.has(out)) return reverbs.get(out);
  const dry = ctx.createGain();
  const send = ctx.createGain();
  const conv = ctx.createConvolver();
  const wet = ctx.createGain();
  conv.buffer = impulse(ctx);
  send.gain.value = 1;
  wet.gain.value = 0.32;
  dry.connect(out);
  send.connect(conv); conv.connect(wet); wet.connect(out);
  const bus = { dry, send };
  reverbs.set(out, bus);
  return bus;
};

const env = (g, t0, peak, attack, decay) => {
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
};

const route = (bus, node, wetAmt) => {
  node.connect(bus.dry);
  const s = node.context.createGain();
  s.gain.value = wetAmt;
  node.connect(s); s.connect(bus.send);
};

let noiseBuf = null;
const noise = (ctx) => {
  if (!noiseBuf || noiseBuf.sampleRate !== ctx.sampleRate) {
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  return src;
};

/* Crystal FM bell — inharmonic shimmer, long natural decay. */
export const bell = (ctx, bus, f, t0, peak = 0.18, decay = 1.8, track) => {
  const car = ctx.createOscillator(); const mod = ctx.createOscillator();
  const modG = ctx.createGain(); const g = ctx.createGain();
  car.frequency.value = f; mod.frequency.value = f * 3.5;
  modG.gain.setValueAtTime(f * 2.2, t0);
  modG.gain.exponentialRampToValueAtTime(f * 0.05, t0 + decay * 0.6);
  mod.connect(modG); modG.connect(car.frequency);
  car.connect(g); env(g, t0, peak, 0.004, decay);
  route(bus, g, 0.6);
  car.start(t0); mod.start(t0); car.stop(t0 + decay + 0.1); mod.stop(t0 + decay + 0.1);
  if (track) track(g, car);
};

/* Harp-like pluck — bright attack that mellows quickly. */
export const pluck = (ctx, bus, f, t0, peak = 0.16, decay = 1.3, track) => {
  const o = ctx.createOscillator(); const lp = ctx.createBiquadFilter(); const g = ctx.createGain();
  o.type = 'triangle'; o.frequency.value = f;
  lp.type = 'lowpass'; lp.frequency.setValueAtTime(5200, t0); lp.frequency.exponentialRampToValueAtTime(700, t0 + decay * 0.7);
  o.connect(lp); lp.connect(g); env(g, t0, peak, 0.003, decay);
  route(bus, g, 0.45);
  o.start(t0); o.stop(t0 + decay + 0.1);
  if (track) track(g, o);
};

/* Velvet card swish — filtered air sweeping across. */
export const swish = (ctx, bus, t0, dur = 0.16, peak = 0.22, from = 900, to = 3800) => {
  const n = noise(ctx); const bp = ctx.createBiquadFilter(); const g = ctx.createGain();
  bp.type = 'bandpass'; bp.Q.value = 1.4;
  bp.frequency.setValueAtTime(from, t0); bp.frequency.exponentialRampToValueAtTime(to, t0 + dur);
  n.connect(bp); bp.connect(g); env(g, t0, peak, dur * 0.35, dur * 0.65);
  route(bus, g, 0.15);
  n.start(t0); n.stop(t0 + dur + 0.05);
};

/* Soft lacquer tap — the card landing on the table. */
export const tap = (ctx, bus, t0, peak = 0.2) => {
  const n = noise(ctx); const lp = ctx.createBiquadFilter(); const g = ctx.createGain();
  lp.type = 'lowpass'; lp.frequency.value = 1500;
  n.connect(lp); lp.connect(g); env(g, t0, peak, 0.002, 0.05);
  route(bus, g, 0.1);
  n.start(t0); n.stop(t0 + 0.08);
  const o = ctx.createOscillator(); const og = ctx.createGain();
  o.frequency.setValueAtTime(210, t0); o.frequency.exponentialRampToValueAtTime(120, t0 + 0.06);
  o.connect(og); env(og, t0, peak * 0.7, 0.002, 0.07);
  route(bus, og, 0.1);
  o.start(t0); o.stop(t0 + 0.1);
};

/* Card riffle — a quick run of swishes and taps (shuffle / deal). */
export const riffle = (ctx, bus, t0, count = 8, gap = 0.055) => {
  for (let i = 0; i < count; i++) {
    const t = t0 + i * gap + Math.random() * 0.012;
    swish(ctx, bus, t, 0.07, 0.12, 1400, 4200);
    tap(ctx, bus, t + 0.05, 0.07);
  }
};

/* Rising harp glissando over a pentatonic scale. */
export const gliss = (ctx, bus, t0, notes, gap = 0.07, peak = 0.13, track) => {
  notes.forEach((f, i) => pluck(ctx, bus, f, t0 + i * gap, peak, 1.4, track));
};
